import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../shared/service/dataprovider';
import tokens from '../../shared/theme/tokens';
import ConfirmDialog from '../../components/ConfirmDialog';
import Panel from '../../components/Panel';
import StatusChip from '../../components/StatusChip';
import { formatIdvFailureDescription } from './idvFailureReason';
import { matchLeaseApplication } from './matchLeaseApplication';

// Read-only preview of one applicant's affordable application, exactly as they
// submitted it. Embedded inside that applicant's resident tab on LeaseDetail.
// Backed by GET /v1/admin/lease/:id/application (one row per linked application,
// each with the raw 11-step affordable `applicationData` blob + presigned
// document view URLs). Steps render as collapsed accordions so the file stays
// scannable. No edits, no mutations.

// The 11 affordable steps, in the order the applicant fills them, mapped to
// human section titles. Mirrors STEP_ORDER in resident-portal-app
// (Affordable/applicationState.ts) and the backend preview payload.
const STEP_ORDER = [
  'authorizationToAssist',
  'memberProfiles',
  'rentalDetails',
  'studentDetails',
  'specialNeeds',
  'specialStatus',
  'householdIncome',
  'assets',
  'financialSummary',
  'uploadCenter',
  'reviewSign',
];
const STEP_TITLES = {
  authorizationToAssist: 'Authorization to Assist',
  memberProfiles: 'Household Members',
  rentalDetails: 'Rental History',
  studentDetails: 'Student Status',
  specialNeeds: 'Special Needs',
  specialStatus: 'Special Status',
  householdIncome: 'Household Income',
  assets: 'Assets',
  financialSummary: 'Financial Summary',
  uploadCenter: 'Documents',
  reviewSign: 'Review & Sign',
};

// A live-in aide submits a different questionnaire (its own sub-flow), so its
// applicationData has a different set of top-level slices. Render those in
// questionnaire order with aide-specific section titles instead of the 11-step
// applicant layout. Mirrors AIDE_STEPS in resident-portal-app
// (Affordable/liveInAideState.ts) and the aide slice keys persisted on the
// aide's prop_application.applicationData. (No `review` slice is persisted.)
const AIDE_STEP_ORDER = ['identity', 'rentalHistory', 'household', 'careAttestations', 'medicalExpense', 'criminalScreening', 'statesLived', 'leasingAck'];
const AIDE_STEP_TITLES = {
  identity: 'Identity & Resident Served',
  rentalHistory: 'Rental History',
  household: 'Household & Eviction',
  careAttestations: 'Care Attestations (HUD)',
  medicalExpense: 'Medical Expense',
  criminalScreening: 'Criminal Screening',
  statesLived: 'States Lived (last 5 yrs)',
  leasingAck: 'Leasing Acknowledgments',
};

// Conventional (market-rate) applications have their own top-level shape (no
// fixed step wizard like affordable). We render the sections that are actually
// present, ordered by this curated priority for readability; any extra keys the
// blob carries are appended so nothing submitted is dropped. Absent sections are
// simply not shown (unlike affordable, there's no canonical must-show step list).
const CONV_STEP_ORDER = [
  'applicantInfo',
  'currentAddress',
  'currentEmployment',
  'incomeAmount',
  'incomeFrequency',
  'noIncome',
  'useCoApplicantIncome',
  'incomeVerification',
  'proofupStatus',
  'hasOtherOccupants',
  'otherApplicants',
  'guarantors',
  'hasVehicles',
  'vehicles',
  'pets',
  'rentableItems',
  'images',
  'plaidIdentityVerification',
  'agreeToReview',
  'agreetoTerms',
];
const CONV_STEP_TITLES = {
  applicantInfo: 'Applicant',
  currentAddress: 'Current Address',
  currentEmployment: 'Employment',
  incomeAmount: 'Income Amount',
  incomeFrequency: 'Income Frequency',
  noIncome: 'No Income Declared',
  useCoApplicantIncome: 'Use Co-Applicant Income',
  incomeVerification: 'Income Verification',
  proofupStatus: 'ProofUp Status',
  hasOtherOccupants: 'Anyone else living here',
  otherApplicants: 'Co-Applicants & Occupants',
  guarantors: 'Guarantors',
  hasVehicles: 'Vehicles to register',
  vehicles: 'Vehicles',
  pets: 'Pets',
  rentableItems: 'Rentable Items',
  images: 'Uploaded Documents',
  plaidIdentityVerification: 'ID Verification (Plaid)',
  agreeToReview: 'Agreed to Review',
  agreetoTerms: 'Agreed to Terms',
};

// True when an application row belongs to the live-in aide sub-flow.
const isAideRole = role => (role ?? '').toLowerCase() === 'live-in aide';

// A few hand-tuned labels; everything else falls back to humanizeKey().
const LABEL_OVERRIDES = {
  ssn: 'SSN',
  willAnyoneElseLive:
    'Will anyone else be living with you or joining your household in the next 12 months?',
  hasVehicles: 'Do you have any vehicles to register?',
  hasOtherOccupants: 'Will anyone else be living here?',
  noSsn: 'No SSN provided',
  govId: 'Government ID #',
  govIdType: 'Government ID type',
  dateOfBirth: 'Date of birth',
  q1_titleIV: 'Title IV student?',
  q2_fosterCare: 'Former foster care?',
  q3_jtpa: 'Job-training (JTPA/WIA)?',
  q4_singleParent: 'Single parent w/ dependents?',
  q5_marriedJointReturn: 'Married, filing jointly?',
  ssiSsdi: 'SSI / SSDI',
  ssRetirement: 'Social Security / retirement',
  vaGiBill: 'VA / GI Bill',
  publicHousingSection8: 'Public Housing / Section 8',
  // Live-In Aide questionnaire fields (auto-humanize reads poorly for these).
  residentServedName: 'Resident receiving services',
  exemptNonCitizen: 'Exempt — non-eligible non-citizen',
  exemptSenior: 'Exempt — senior',
  hudAssistance: 'Receiving HUD assistance',
  otherFamilyMoving: 'Other family moving in',
  goodStanding: 'Good standing at current residence',
  evicted: 'Evicted (last 5 yrs)',
  evictedExplain: 'Eviction — explanation',
  essentialCare: 'Provides essential care',
  obligatedSupport: 'Obligated to resident for support',
  wouldNotLiveExcept: 'Would not live in unit except to provide care',
  residentPaysFee: 'Resident pays aide a fee',
  legalActions10y: 'Legal actions (last 10 yrs)',
  legalActionsExplain: 'Legal actions — explanation',
  sexOffenderLifetime: 'Lifetime sex-offender registry',
  sexOffenderOther: 'Other sex-offender registry',
  alcoholAbuse: 'History of alcohol abuse',
  marijuana: 'Uses marijuana',
  readLease: 'Read & understands lease',
  agreeAbide: 'Agrees to abide by lease / rules',
  understandEviction: 'Understands eviction for non-compliance',
  noOccupancyRights: 'No continued-occupancy rights',
  vacateIfAbsent: 'Will vacate if resident absent >120/180 days',
  vacateOnDeath: 'Will vacate promptly on resident death',
};

// camelCase / snake_case / colon-delimited → "Title Case".
function humanizeKey(key) {
  const base = LABEL_OVERRIDES[key];
  if (base) return base;
  return key
    .replace(/[_:]/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, c => c.toUpperCase());
}

const isSsnKey = key => key && key.toLowerCase() === 'ssn';
const isGovIdKey = key => key && key.toLowerCase() === 'govid';

// Mask SSN to last-4 for the default review render (PII hygiene). Accepts the
// 9-digit string the form stores; falls back to a fully masked value otherwise.
function maskSsn(value) {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length >= 4) return `•••-••-${digits.slice(-4)}`;
  return '•••-••-••••';
}

// Mask an arbitrary government ID (driver's license, passport, etc.) to its
// last-4 chars. Unlike SSN it is not fixed-format, so keep the trailing 4 of
// the raw string rather than digits-only.
function maskGovId(value) {
  const tail = String(value).slice(-4);
  return tail ? `••••${tail}` : '••••';
}

const MONEY_KEY = /amount$|income$|value$|rent$/i;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const isBlank = v => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

// ISO date/datetime string → friendly text. Date-only ("1978-07-10") →
// "Jul 10, 1978"; full timestamp → "Jun 11, 2026 2:56 AM". Returns null when the
// value isn't a recognizable ISO date so callers can fall through.
function formatMaybeDate(value) {
  if (typeof value !== 'string') return null;
  if (ISO_DATETIME.test(value)) {
    const m = moment(value, moment.ISO_8601, true);
    if (m.isValid()) return m.format('MMM D, YYYY h:mm A');
  }
  if (ISO_DATE.test(value)) {
    const m = moment(value, 'YYYY-MM-DD', true);
    if (m.isValid()) return m.format('MMM D, YYYY');
  }
  return null;
}

// Human-readable file size from a byte count.
function formatBytes(n) {
  if (typeof n !== 'number' || !isFinite(n)) return String(n);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Render a scalar value as display text. Booleans → Yes/No, SSN/govId masked,
// ISO dates formatted, money-ish numerics get a $, everything else stringified.
function formatScalar(key, value) {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === 'yes') return 'Yes';
  if (value === 'no') return 'No';
  if (isSsnKey(key)) return maskSsn(value);
  if (isGovIdKey(key)) return maskGovId(value);
  const asDate = formatMaybeDate(value);
  if (asDate) return asDate;
  if (typeof value === 'number' && MONEY_KEY.test(key)) {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
  return String(value);
}

// An uploaded document, as stored in uploadCenter.documents[type][] and enriched
// server-side with a presigned `viewUrl`.
const isFileObject = v => v && typeof v === 'object' && !Array.isArray(v) && typeof v.s3Key === 'string' && typeof v.fileName === 'string';

const Muted = ({ children }) => (
  <Typography component="span" variant="body2" sx={{ color: tokens.colors.inkFaint, fontStyle: 'italic' }}>
    {children}
  </Typography>
);

// A single uploaded file: filename links to the presigned doc, with size +
// upload time as muted metadata. Falls back to plain text if no view URL.
function FileLink({ file }) {
  const meta = [file.fileSize != null && formatBytes(file.fileSize), file.uploadedAt && `uploaded ${formatMaybeDate(file.uploadedAt) ?? file.uploadedAt}`]
    .filter(Boolean)
    .join(' · ');
  return (
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ py: 0.25, flexWrap: 'wrap' }}>
      {file.viewUrl ? (
        <Link href={file.viewUrl} target="_blank" rel="noopener" underline="hover" sx={{ fontSize: 14 }}>
          {file.fileName}
        </Link>
      ) : (
        <Typography variant="body2">{file.fileName}</Typography>
      )}
      {meta && <Muted>({meta})</Muted>}
    </Stack>
  );
}

// One labelled field row: label on the left, value on the right.
function FieldRow({ label, children }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ py: 0.5 }}>
      <Typography variant="body2" sx={{ color: tokens.colors.inkSecondary, minWidth: 150, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>{typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}</Box>
    </Stack>
  );
}

// Teal sub-section label for a nested group (design-system sub-header spec:
// 14px / 600 / teal). `muted` variant (grey) is used for array item indices.
function SubHeading({ children, muted }) {
  return (
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: muted ? tokens.colors.inkSecondary : tokens.colors.primary, mt: 1, mb: 0.25 }}>
      {children}
    </Typography>
  );
}

// Plaid dashboard deep link for one IDV session. The external session id looks
// like `idvtmp_5NGHfmmdhU2j84`; the dashboard route takes it verbatim.
const PLAID_IDV_SESSION_BASE = 'https://dashboard.plaid.com/identity_verification/sessions/';
export const plaidIdvSessionUrl = sid => `${PLAID_IDV_SESSION_BASE}${encodeURIComponent(sid)}`;

// Plaid IDV session status → StatusChip tone.
const IDV_STATUS_TONE = {
  success: 'success',
  failed: 'error',
  expired: 'neutral',
  canceled: 'neutral',
  pending: 'warning',
  active: 'info',
  pending_review: 'info',
};
export const idvStatusTone = status => IDV_STATUS_TONE[String(status || '').toLowerCase()] || 'neutral';

// SUCCESS → "Success", PENDING_REVIEW → "Pending Review".
export const idvStatusLabel = status =>
  String(status || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

// The verification steps Plaid marked "failed" on the non-PII failure snapshot,
// as human labels. Empty when the session didn't fail or has no snapshot. Never
// exposes the underlying PII — only which step failed.
export function idvFailedSteps(failureDetails) {
  const steps = failureDetails && typeof failureDetails === 'object' ? failureDetails.steps : null;
  if (!steps || typeof steps !== 'object') return [];
  return Object.entries(steps)
    .filter(([, v]) => v === 'failed')
    .map(([k]) => humanizeKey(k));
}

// One IDV session row: status chip, a link to the Plaid dashboard session, the
// plain-English failure description (when FAILED), and when it was created.
function IdvSessionRow({ session }) {
  const failureDescription = formatIdvFailureDescription(session);
  const when = session.createdAt ? formatMaybeDate(session.createdAt) ?? session.createdAt : null;
  return (
    <Stack spacing={0.25} sx={{ py: 0.4 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
        <StatusChip status={session.status} label={idvStatusLabel(session.status)} tone={idvStatusTone(session.status)} size="small" />
        {session.externalSessionId ? (
          <Link href={plaidIdvSessionUrl(session.externalSessionId)} target="_blank" rel="noopener" underline="hover" sx={{ fontSize: 14 }}>
            {session.externalSessionId}
          </Link>
        ) : (
          <Muted>no session id</Muted>
        )}
        {when && <Muted>({when})</Muted>}
      </Stack>
      {failureDescription ? (
        <Typography variant="caption" color="error.main" sx={{ pl: 0.25 }}>
          {failureDescription}
        </Typography>
      ) : null}
    </Stack>
  );
}

// The "ID Verification (Plaid)" section. Shows the applicant's submitted status
// scalar (when present) plus every Plaid IDV session on file (all attempts /
// retries), each deep-linked to the Plaid dashboard. Rendered as an accordion to
// match the other step sections.
function IdvStepSection({ submittedStatus, sessions = [] }) {
  const title = CONV_STEP_TITLES.plaidIdentityVerification;
  const hasStatus = !isBlank(submittedStatus);
  if (!hasStatus && sessions.length === 0) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1, px: 1.5, borderTop: `1px solid ${tokens.colors.border}` }}>
        <Typography variant="subtitle2" sx={{ color: tokens.colors.inkFaint, fontWeight: 700, flexGrow: 1 }}>
          {title}
        </Typography>
        <Muted>Not provided</Muted>
      </Stack>
    );
  }
  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      sx={{ borderTop: `1px solid ${tokens.colors.border}`, '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
        <Typography variant="subtitle2" sx={{ color: tokens.colors.primary, fontWeight: 700 }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0 }}>
        {hasStatus && <FieldRow label="Status">{formatScalar('plaidIdentityVerification', submittedStatus)}</FieldRow>}
        {sessions.length > 0 ? (
          <Box sx={{ mt: hasStatus ? 1 : 0 }}>
            <SubHeading>Plaid sessions</SubHeading>
            <Stack spacing={0.25}>
              {sessions.map((s, i) => (
                <IdvSessionRow key={s.externalSessionId || i} session={s} />
              ))}
            </Stack>
          </Box>
        ) : (
          <Muted>No Plaid verification sessions on file.</Muted>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// True when a value renders as a nested block (an object, or an array of
// objects/files) rather than a single scalar that fits a grid cell. Blanks are
// simple ("Not provided").
function isComplexValue(v) {
  if (isBlank(v)) return false;
  if (Array.isArray(v)) return !v.every(x => typeof x !== 'object' || x === null);
  return typeof v === 'object';
}

// Recursively render an arbitrary value from the application blob. File objects
// become download links, arrays-of-objects become numbered sub-cards, arrays of
// scalars become a comma list, objects become nested groups, scalars a line.
function renderValue(key, value, depth) {
  if (isBlank(value)) return <Muted>Not provided</Muted>;

  if (Array.isArray(value)) {
    const allScalar = value.every(v => typeof v !== 'object' || v === null);
    if (allScalar) return formatScalarList(value);
    if (value.every(isFileObject)) {
      return (
        <Stack spacing={0.25}>
          {value.map((f, i) => (
            <FileLink key={i} file={f} />
          ))}
        </Stack>
      );
    }
    return (
      <Box>
        {value.map((item, i) => (
          <Box key={i} sx={{ mt: i === 0 ? 0 : 1.5 }}>
            {value.length > 1 && <SubHeading muted>#{i + 1}</SubHeading>}
            {renderObject(item, depth + 1)}
          </Box>
        ))}
      </Box>
    );
  }

  if (isFileObject(value)) return <FileLink file={value} />;

  if (typeof value === 'object') return renderObject(value, depth + 1);

  return formatScalar(key, value);
}

function formatScalarList(arr) {
  const shown = arr.filter(v => !isBlank(v));
  if (shown.length === 0) return <Muted>Not provided</Muted>;
  return shown.map(v => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v))).join(', ');
}

// Render an object's fields. Simple scalars go into a responsive two-column grid
// to keep the height down; nested groups (objects / arrays of objects) drop to
// their own full-width block under a teal sub-heading, on the next line.
function renderObject(obj, depth) {
  // Guard: an object-array can contain a null/scalar hole (renderValue calls us
  // per item). Object.entries(null) would throw and blank the whole panel.
  if (!obj || typeof obj !== 'object') return <Muted>Not provided</Muted>;
  const entries = Object.entries(obj).filter(([k]) => !k.startsWith('_'));
  if (entries.length === 0) return <Muted>Not provided</Muted>;
  const scalars = entries.filter(([, v]) => !isComplexValue(v));
  const groups = entries.filter(([, v]) => isComplexValue(v));
  return (
    <Box>
      {scalars.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 4 }}>
          {scalars.map(([k, v]) => (
            <FieldRow key={k} label={humanizeKey(k)}>
              {renderValue(k, v, depth)}
            </FieldRow>
          ))}
        </Box>
      )}
      {groups.map(([k, v]) => (
        <Box key={k} sx={{ mt: 1 }}>
          <SubHeading>{humanizeKey(k)}</SubHeading>
          <Box sx={{ pl: 1.5, borderLeft: `2px solid ${tokens.colors.border}` }}>{renderValue(k, v, depth)}</Box>
        </Box>
      ))}
    </Box>
  );
}

// One step. `data` is the step's slice of applicationData. Populated steps are a
// collapsed accordion (keeps the file short — expand to read); empty steps are a
// compact muted row so the reader still sees the step was offered.
function StepSection({ stepKey, data, titles = STEP_TITLES }) {
  const title = titles[stepKey] ?? humanizeKey(stepKey);
  if (isBlank(data)) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1, px: 1.5, borderTop: `1px solid ${tokens.colors.border}` }}>
        <Typography variant="subtitle2" sx={{ color: tokens.colors.inkFaint, fontWeight: 700, flexGrow: 1 }}>
          {title}
        </Typography>
        <Muted>Not provided</Muted>
      </Stack>
    );
  }
  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      sx={{ borderTop: `1px solid ${tokens.colors.border}`, '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
        <Typography variant="subtitle2" sx={{ color: tokens.colors.primary, fontWeight: 700 }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0 }}>
        {typeof data === 'object' ? renderObject(data, 0) : <Typography variant="body2">{String(data)}</Typography>}
      </AccordionDetails>
    </Accordion>
  );
}

// One application's steps. Three layouts:
//  - live-in aide sub-flow: its own slice set + titles (fixed list + placeholders);
//  - affordable applicant: the 11-step affordable layout (fixed list + placeholders);
//  - conventional (market-rate): render the sections actually present, ordered by
//    CONV_STEP_ORDER priority, then any extras — no placeholders for absent sections.
// Extra keys present in the blob but unknown to the layout are always appended so
// nothing the applicant submitted is silently dropped.
const IGNORE_KEYS = new Set(['language']); // top-level bookkeeping, not a step
// Exported so the no-lease application view (pages/ApplicationDetail) can render
// the exact same "as submitted" layout for a single in-progress/stuck application.
export function ApplicationSteps({ app, isAffordable, idvSessions = [] }) {
  const data = app.applicationData ?? {};
  const aide = isAideRole(app.role);
  const presentKeys = Object.keys(data).filter(k => !IGNORE_KEYS.has(k) && !k.startsWith('_'));

  let order;
  let titles;
  let showPlaceholders; // affordable/aide show the full step list (absent => "Not provided")
  if (aide) {
    order = AIDE_STEP_ORDER;
    titles = AIDE_STEP_TITLES;
    showPlaceholders = true;
  } else if (isAffordable) {
    order = STEP_ORDER;
    titles = STEP_TITLES;
    showPlaceholders = true;
  } else {
    // Conventional: known sections first (curated order), then any extras; present only.
    titles = CONV_STEP_TITLES;
    const known = CONV_STEP_ORDER.filter(k => presentKeys.includes(k));
    const rest = presentKeys.filter(k => !CONV_STEP_ORDER.includes(k));
    order = [...known, ...rest];
    showPlaceholders = false;
  }

  const knownKeys = new Set(order);
  const extraKeys = showPlaceholders ? presentKeys.filter(k => !knownKeys.has(k)) : [];

  // The Plaid IDV section is rendered with a dedicated component so it can link
  // out to the dashboard and list every session; every other step uses the
  // generic StepSection.
  const renderSection = stepKey =>
    stepKey === 'plaidIdentityVerification' ? (
      <IdvStepSection key={stepKey} submittedStatus={data[stepKey]} sessions={idvSessions} />
    ) : (
      <StepSection key={stepKey} stepKey={stepKey} data={data[stepKey]} titles={titles} />
    );

  // Surface IDV sessions even when the applicant blob carries no
  // plaidIdentityVerification key (e.g. affordable flow, or the applicant never
  // reached that step) — as long as sessions actually exist.
  const orderHasIdv = order.includes('plaidIdentityVerification') || extraKeys.includes('plaidIdentityVerification');

  return (
    <Box sx={{ border: `1px solid ${tokens.colors.border}`, borderTop: 'none', borderRadius: 1 }}>
      {order.map(renderSection)}
      {extraKeys.map(renderSection)}
      {!orderHasIdv && idvSessions.length > 0 && <IdvStepSection submittedStatus={undefined} sessions={idvSessions} />}
    </Box>
  );
}

export const roleTone = role => ((role ?? '').toLowerCase() === 'head of household' ? 'info' : 'neutral');

export default function ApplicationPreviewPanel({ lease, leaseLink }) {
  const isAffordable = Boolean(lease?.isAffordable);
  const { data, isLoading, error } = useQuery({
    queryKey: [`leaseApplication/${lease.id}`],
    queryFn: () => api.get(`/v1/admin/lease/${lease.id}/application`),
  });
  const applications = data?.data?.applications ?? [];
  const app = matchLeaseApplication(applications, {
    email: leaseLink?.people?.Email,
    personId: leaseLink?.people?.id,
  });

  const queryClient = useQueryClient();
  const [discardOpen, setDiscardOpen] = useState(false);
  const applicationId = app?.applicationId ?? app?.id;
  // Reopen THIS applicant's affordable application for a pre-sign correction: flips it
  // to pending_resubmit and returns a deep link into the resident editor (the full
  // wizard for the HoH, the co-applicant editor for an adult co-applicant), which staff
  // drive under their own session. Idempotent — the backend enforces the pre-packet
  // gate (409 once the signing package exists) and re-validates the applicant is
  // correctable. Sends the applicant's own applicationId so the right row is reopened.
  const reopenMutation = useMutation({
    mutationFn: () => api.post(`/v1/admin/lease/${lease.id}/reopen-affordable`, { applicationId }),
    onSuccess: res => {
      const editorUrl = res?.data?.editorUrl;
      if (editorUrl) window.open(editorUrl, '_blank', 'noopener');
      toast.success('Reopened for correction — the resident editor opens in a new tab.');
      queryClient.invalidateQueries({ queryKey: [`leaseApplication/${lease.id}`] });
    },
    onError: e => toast.error(e?.response?.data?.message ?? 'Could not reopen this application for correction.'),
  });

  // Abandon an in-flight correction: restore the last committed submission and revoke
  // the correction grant. Distinct from cancel — does not terminate the application.
  const discardMutation = useMutation({
    mutationFn: () => api.post(`/v1/admin/applications/${applicationId}/discard-correction`),
    onSuccess: () => {
      setDiscardOpen(false);
      toast.success('Correction edits discarded. Application restored to the last submission.');
      queryClient.invalidateQueries({ queryKey: [`leaseApplication/${lease.id}`] });
    },
    onError: e => toast.error(e?.response?.data?.message ?? 'Could not discard current edits.'),
  });

  // Correctable applicants are the lease SIGNERS — Head of Household + adult
  // co-applicants (mirrors CORRECTABLE_AFFORDABLE_ROLES on the API); minors and
  // live-in aides are excluded. Only before the signing package is generated
  // (submitted/completed => reopenable; already reopened => continue). The backend
  // is the source of truth for the pre-packet gate + ops scope.
  const CORRECTABLE_ROLES = ['head of household', 'family member - adult', 'spouse/cohabitant'];
  const isCorrectableRole = app && (!app.role || CORRECTABLE_ROLES.includes(String(app.role).toLowerCase()));
  const canReopen = isAffordable && isCorrectableRole && ['submitted', 'completed', 'pending_resubmit'].includes(app?.status);
  const reopenLabel = app?.status === 'pending_resubmit' ? 'Continue correction' : 'Reopen for correction';
  // Only while a correction is in flight. Do not offer hard "Cancel application" on
  // this preview — that belongs on Application Detail; here it reads as wiping the app.
  const canDiscard = Boolean(applicationId) && app?.status === 'pending_resubmit';

  return (
    <Panel sx={{ mt: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="h4">Submitted Application</Typography>
        {app?.role && <StatusChip status={app.role} tone={roleTone(app.role)} size="small" />}
        {app?.status && <StatusChip status={app.status} size="small" />}
        {app?.updatedDate && (
          <Typography variant="caption" sx={{ color: tokens.colors.inkFaint }}>
            Last updated {moment(app.updatedDate).format('MMM D, YYYY h:mm A')}
          </Typography>
        )}
        {(canReopen || canDiscard) && (
          <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
            {canReopen && (
              <Button size="small" variant="outlined" disabled={reopenMutation.isPending} onClick={() => reopenMutation.mutate()}>
                {reopenMutation.isPending ? 'Opening…' : reopenLabel}
              </Button>
            )}
            {canDiscard && (
              <Button size="small" variant="outlined" disabled={discardMutation.isPending} onClick={() => setDiscardOpen(true)}>
                Discard current edits
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      <Typography variant="body2" sx={{ color: tokens.colors.inkFaint, mb: 2 }}>
        Read-only view of everything this applicant submitted, exactly as entered.
        {isAffordable ? ' Verified income amounts live on the Income Verification tab.' : ''}
      </Typography>

      {isLoading && <CircularProgress size={20} />}

      {error && (
        <Typography sx={{ color: tokens.colors.error.fg }}>{error?.response?.data?.message ?? 'Could not load the application. Try again later.'}</Typography>
      )}

      {!isLoading && !error && !app && <Typography sx={{ color: tokens.colors.inkFaint }}>No application on file for this applicant yet.</Typography>}

      {!isLoading && !error && app && !app.applicationData && (
        <Typography sx={{ color: tokens.colors.inkFaint }}>This applicant hasn’t started their application yet.</Typography>
      )}

      {app?.applicationData?.uploadCenter?.needsDocumentHelp && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            backgroundColor: tokens.colors.warning.bg,
            color: tokens.colors.warning.fg,
            border: `1px solid ${tokens.colors.warning.main}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ⚠️ This applicant asked for help uploading their documents.
          </Typography>
        </Box>
      )}

      {app?.applicationData && <ApplicationSteps app={app} isAffordable={isAffordable} idvSessions={app.idvSessions ?? []} />}

      <ConfirmDialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        title="Discard current edits?"
        body="This discards the in-progress correction and restores the last submitted application. The application itself stays active."
        cancelLabel="Keep editing"
        confirmLabel="Discard current edits"
        loading={discardMutation.isPending}
        onConfirm={() => discardMutation.mutate()}
      />
    </Panel>
  );
}
